import React from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Button,
  SimpleGrid,
  VStack } from
'@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  ShieldAlert,
  Search,
  Activity,
  Bell,
  TrendingUp,
  XCircle,
  CheckCircle2,
  Layers,
  Wrench,
  ShieldCheck,
  Briefcase,
  Radar } from
'lucide-react';
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
export function RadarLandingPage() {
  const navigate = useNavigate();
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.5
    }
  };
  const handleScanNavigate = () => {
    navigate('/lander');
    window.scrollTo(0, 0);
  };
  return (
    <Box as="main" overflow="hidden">
      {/* 1. HERO SECTION */}
      <Box
        as="section"
        position="relative"
        pt={{
          base: 20,
          lg: 32
        }}
        pb={{
          base: 24,
          lg: 32
        }}>
        
        <Box
          position="absolute"
          top={0}
          left="50%"
          transform="translateX(-50%)"
          w="800px"
          h="400px"
          bg="rgba(16,185,129,0.15)"
          filter="blur(120px)"
          borderRadius="full"
          pointerEvents="none" />
        

        <Container maxW="container.xl" px={4} position="relative" zIndex={10}>
          <Box maxW="4xl" mx="auto" textAlign="center">
            <MotionBox {...fadeIn}>
              <Heading
                as="h1"
                fontSize={{
                  base: '2xl',
                  sm: '3xl',
                  md: '5xl',
                  lg: '6xl'
                }}
                fontWeight="bold"
                letterSpacing="tight"
                mb={6}
                lineHeight="1.1">
                
                See Which Products Will Get Your Store Banned —{' '}
                <Text as="span" color="brand.green">
                  Before Google Does
                </Text>
              </Heading>
            </MotionBox>

            <MotionBox
              {...fadeIn}
              transition={{
                delay: 0.1
              }}>
              
              <Text
                fontSize={{
                  base: 'lg',
                  md: 'xl'
                }}
                color="gray.400"
                mb={10}
                maxW="3xl"
                mx="auto"
                lineHeight="relaxed">
                
                Most suspensions don't come from your whole store… They come
                from{' '}
                <Text as="strong" color="white">
                  one risky product
                </Text>
                . GMCRadar shows you exactly which products are putting your
                Google Merchant Center accounts at risk — across all your
                stores.
              </Text>
            </MotionBox>

            <MotionBox
              {...fadeIn}
              transition={{
                delay: 0.2
              }}>
              
              <Flex
                direction={{
                  base: 'column',
                  sm: 'row'
                }}
                align="center"
                justify="center"
                gap={4}
                mb={16}>
                
                <Button
                  as={RouterLink}
                  to="/request-access"
                  onClick={() => window.scrollTo(0, 0)}
                  w={{
                    base: 'full',
                    sm: 'auto'
                  }}
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                  px={8}
                  py={4}
                  h="auto"
                  fontSize="lg"
                  fontWeight="bold"
                  color="brand.dark"
                  bg="brand.green"
                  borderRadius="xl"
                  _hover={{
                    bg: 'brand.greenHover',
                    boxShadow: 'glow-strong',
                    transform: 'scale(1.05)'
                  }}
                  transition="all 0.2s">
                  
                  <Text as="span">Request Access</Text>
                  <ArrowRight size={20} />
                </Button>
                {/* <Button
                  onClick={handleScanNavigate}
                  w={{
                    base: 'full',
                    sm: 'auto'
                  }}
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                  px={8}
                  py={4}
                  h="auto"
                  fontSize="lg"
                  fontWeight="bold"
                  color="white"
                  bg="transparent"
                  border="2px solid"
                  borderColor="brand.border"
                  borderRadius="xl"
                  _hover={{
                    borderColor: 'brand.green',
                    bg: 'rgba(16,185,129,0.05)'
                  }}
                  transition="all 0.2s">
                  
                  <Text as="span">Scan My Store</Text>
                  <Search size={20} />
                </Button> */}
              </Flex>
            </MotionBox>

            {/* Dashboard Mock */}
            <MotionBox
              {...fadeIn}
              transition={{
                delay: 0.3
              }}
              position="relative"
              maxW="3xl"
              mx="auto">
              
              <Box
                position="absolute"
                inset={0}
                bgGradient="linear(to-t, brand.dark, transparent, transparent)"
                zIndex={10}
                pointerEvents="none" />
              
              <Box
                bg="brand.card"
                border="1px solid"
                borderColor="brand.border"
                borderRadius="2xl"
                p={{
                  base: 4,
                  md: 6
                }}
                boxShadow="glow"
                textAlign="left">
                
                <Flex
                  align="center"
                  justify="space-between"
                  mb={6}
                  borderBottom="1px solid"
                  borderColor="brand.border"
                  pb={4}>
                  
                  <Flex align="center" gap={2}>
                    <Layers color="#10b981" size={20} />
                    <Text fontWeight="semibold" color="white">
                      Multi-Store Overview
                    </Text>
                  </Flex>
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    bg="brand.dark"
                    px={2}
                    py={1}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="brand.border"
                    color="gray.400">
                    
                    Live Sync
                  </Text>
                </Flex>

                <VStack spacing={3} align="stretch">
                  {[
                  {
                    name: 'Store 1 (US)',
                    score: 68,
                    status: 'At Risk',
                    color: '#f87171',
                    bg: 'rgba(248,113,113,0.1)',
                    borderC: 'rgba(248,113,113,0.2)'
                  },
                  {
                    name: 'Store 2 (UK)',
                    score: 92,
                    status: 'Healthy',
                    color: '#10b981',
                    bg: 'rgba(16,185,129,0.1)',
                    borderC: 'rgba(16,185,129,0.2)'
                  },
                  {
                    name: 'Store 3 (EU)',
                    score: 85,
                    status: 'Warning',
                    color: '#facc15',
                    bg: 'rgba(250,204,21,0.1)',
                    borderC: 'rgba(250,204,21,0.2)'
                  }].
                  map((store, i) =>
                  <Flex
                    key={i}
                    align="center"
                    justify="space-between"
                    p={3}
                    bg="brand.dark"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="brand.border">
                    
                      <Text fontWeight="medium" color="gray.200">
                        {store.name}
                      </Text>
                      <Flex align="center" gap={4}>
                        <Box
                        display={{
                          base: 'none',
                          sm: 'block'
                        }}
                        w={32}
                        h={2}
                        bg="brand.card"
                        borderRadius="full"
                        overflow="hidden">
                        
                          <Box
                          h="full"
                          bg={store.color}
                          w={`${store.score}%`} />
                        
                        </Box>
                        <Text fontSize="sm" fontFamily="mono" color="gray.400">
                          {store.score}/100
                        </Text>
                        <Text
                        fontSize="xs"
                        fontWeight="bold"
                        px={2}
                        py={1}
                        borderRadius="md"
                        border="1px solid"
                        bg={store.bg}
                        color={store.color}
                        borderColor={store.borderC}>
                        
                          {store.status}
                        </Text>
                      </Flex>
                    </Flex>
                  )}
                </VStack>
              </Box>
            </MotionBox>
          </Box>
        </Container>
      </Box>

      {/* 2. RISK PREVIEW BLOCK */}
      <Box
        as="section"
        py={20}
        bg="brand.card"
        borderY="1px solid"
        borderColor="brand.border"
        position="relative">
        
        <Container maxW="container.xl" px={4}>
          <Box maxW="3xl" mx="auto" textAlign="center" mb={12}>
            <MotionBox {...fadeIn}>
              <Heading
                as="h2"
                fontSize={{
                  base: '3xl',
                  md: '4xl'
                }}
                fontWeight="bold"
                mb={4}>
                
                One Product Can Shut Everything Down
              </Heading>
            </MotionBox>
          </Box>

          <MotionBox
            {...fadeIn}
            maxW="2xl"
            mx="auto"
            bg="brand.dark"
            border="1px solid"
            borderColor="rgba(239,68,68,0.3)"
            borderRadius="2xl"
            p={{
              base: 6,
              md: 8
            }}
            boxShadow="0 0 40px -10px rgba(239,68,68,0.2)"
            position="relative"
            overflow="hidden">
            
            <Box
              position="absolute"
              top={0}
              right={0}
              w={32}
              h={32}
              bg="rgba(239,68,68,0.1)"
              filter="blur(50px)"
              borderRadius="full"
              pointerEvents="none" />
            

            <Flex
              flexWrap="wrap"
              align="center"
              justify="space-between"
              gap={4}
              mb={8}
              borderBottom="1px solid"
              borderColor="brand.border"
              pb={6}>
              
              <Box>
                <Text fontSize="sm" color="gray.400" mb={1}>
                  Store Name
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="white">
                  Store 1
                </Text>
              </Box>
              <Box textAlign="right">
                <Text fontSize="sm" color="gray.400" mb={1}>
                  Risk Score
                </Text>
                <Flex align="center" gap={2}>
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color="red.400"
                    fontFamily="mono">
                    
                    68/100
                  </Text>
                  <AlertTriangle color="#f87171" size={24} />
                </Flex>
              </Box>
              <Box
                bg="rgba(239,68,68,0.1)"
                border="1px solid"
                borderColor="rgba(239,68,68,0.3)"
                color="red.400"
                fontWeight="bold"
                px={4}
                py={2}
                borderRadius="lg"
                textTransform="uppercase"
                letterSpacing="wider"
                fontSize="sm">
                
                At Risk
              </Box>
            </Flex>

            <Text
              fontSize="sm"
              fontWeight="bold"
              color="gray.400"
              textTransform="uppercase"
              letterSpacing="wider"
              mb={4}>
              
              High-Risk Products Detected:
            </Text>

            <VStack spacing={3} mb={8} align="stretch">
              {[
              {
                name: '"Fat Burner Pro+"',
                issue: 'Aggressive claims'
              },
              {
                name: '"Posture Corrector X"',
                issue: 'Review mismatch'
              },
              {
                name: '"Sleep Aid Drops"',
                issue: 'Policy violation'
              }].
              map((prod, i) =>
              <Flex
                key={i}
                direction={{
                  base: 'column',
                  sm: 'row'
                }}
                align={{
                  sm: 'center'
                }}
                justify="space-between"
                p={4}
                bg="brand.card"
                border="1px solid"
                borderColor="brand.border"
                borderRadius="lg">
                
                  <Text
                  fontWeight="medium"
                  color="white"
                  mb={{
                    base: 2,
                    sm: 0
                  }}>
                  
                    {prod.name}
                  </Text>
                  <Flex
                  align="center"
                  gap={2}
                  color="red.400"
                  fontSize="sm"
                  fontWeight="medium">
                  
                    <ArrowRight size={14} />
                    <Text>{prod.issue}</Text>
                  </Flex>
                </Flex>
              )}
            </VStack>

            <Box
              textAlign="center"
              pt={6}
              borderTop="1px solid"
              borderColor="brand.border">
              
              <Text fontSize="lg" fontWeight="medium" color="gray.300">
                You don't need 100 bad products.{' '}
                <Text as="strong" color="red.400">
                  Just one is enough.
                </Text>
              </Text>
            </Box>
          </MotionBox>
        </Container>
      </Box>

      {/* 3. PAIN SECTION */}
      <Box as="section" py={24} position="relative">
        <Container maxW="container.xl" px={4}>
          <MotionBox {...fadeIn} maxW="3xl" mx="auto" textAlign="center">
            <Heading
              as="h2"
              fontSize={{
                base: '3xl',
                md: '4xl'
              }}
              fontWeight="bold"
              mb={12}>
              
              Your Store Doesn't Get Banned.{' '}
              <Text as="span" color="red.400">
                Your Products Do.
              </Text>
            </Heading>

            <VStack
              spacing={8}
              fontSize={{
                base: 'xl',
                md: '2xl'
              }}
              fontWeight="medium"
              color="gray.300">
              
              <Text>Most people think:</Text>
              <Text color="white" fontStyle="italic">
                "My store policies look fine."
              </Text>
              <Text>But Google doesn't review your store like you do.</Text>
              <Text>
                It flags specific products — and once enough risk builds up…
              </Text>

              <VStack
                py={8}
                spacing={4}
                color="red.400"
                fontWeight="bold"
                fontSize={{
                  base: '2xl',
                  md: '3xl'
                }}>
                
                <Text>👉 your GMC gets Misrepresentation</Text>
                <Text>👉 your ads stop instantly</Text>
                <Text>👉 your revenue disappears</Text>
              </VStack>

              <Text>No clear explanation. No warning.</Text>
              <Text>Just one day… it's gone.</Text>

              <Box
                mt={12}
                p={6}
                bg="rgba(16,185,129,0.1)"
                border="1px solid"
                borderColor="rgba(16,185,129,0.2)"
                borderRadius="xl"
                display="inline-block">
                
                <Text color="brand.green" fontWeight="bold">
                  GMCRadar shows you which products are dangerous — before
                  Google acts.
                </Text>
              </Box>
            </VStack>
          </MotionBox>
        </Container>
      </Box>

      {/* 4. HOW IT WORKS */}
      <Box
        as="section"
        py={24}
        bg="brand.card"
        borderY="1px solid"
        borderColor="brand.border">
        
        <Container maxW="container.xl" px={4}>
          <MotionBox {...fadeIn} textAlign="center" mb={16}>
            <Heading
              as="h2"
              fontSize={{
                base: '3xl',
                md: '4xl'
              }}
              fontWeight="bold">
              
              How It Works
            </Heading>
          </MotionBox>

          <SimpleGrid
            columns={{
              base: 1,
              md: 2,
              lg: 4
            }}
            spacing={8}
            maxW="5xl"
            mx="auto">
            
            {[
            {
              num: '1',
              title: 'Connect Your GMC Accounts',
              desc: 'View all your stores in one place, no more logging into multiple Google accounts.',
              icon: Layers
            },
            {
              num: '2',
              title: 'Scan Your Products',
              desc: 'We analyse your live product pages like Google does.',
              icon: Search
            },
            {
              num: '3',
              title: 'Detect Risky Listings',
              desc: 'Find products with compliance issues, misleading signals, or policy risks.',
              icon: ShieldAlert
            },
            {
              num: '4',
              title: 'Monitor Your Risk',
              desc: 'Track which stores and which products are moving toward suspension.',
              icon: Activity
            }].
            map((step, i) =>
            <MotionBox
              key={i}
              initial={{
                opacity: 0,
                y: 20
              }}
              whileInView={{
                opacity: 1,
                y: 0
              }}
              viewport={{
                once: true
              }}
              transition={{
                delay: i * 0.1
              }}
              bg="brand.dark"
              border="1px solid"
              borderColor="brand.border"
              borderRadius="2xl"
              p={6}
              position="relative">
              
                <Flex
                position="absolute"
                top={-4}
                left={-4}
                w={8}
                h={8}
                borderRadius="full"
                bg="brand.green"
                color="brand.dark"
                fontWeight="bold"
                align="center"
                justify="center"
                fontSize="sm">
                
                  {step.num}
                </Flex>
                <Flex
                w={12}
                h={12}
                borderRadius="xl"
                bg="brand.card"
                border="1px solid"
                borderColor="brand.border"
                align="center"
                justify="center"
                mb={6}>
                
                  <step.icon color="#10b981" size={24} />
                </Flex>
                <Heading
                as="h3"
                fontSize="lg"
                fontWeight="bold"
                color="white"
                mb={3}>
                
                  {step.title}
                </Heading>
                <Text color="gray.400" fontSize="sm" lineHeight="relaxed">
                  {step.desc}
                </Text>
              </MotionBox>
            )}
          </SimpleGrid>
        </Container>
      </Box>

      {/* 5. FEATURES */}
      <Box as="section" py={24} position="relative">
        <Container maxW="container.xl" px={4}>
          <SimpleGrid
            columns={{
              base: 1,
              md: 2
            }}
            spacingX={12}
            spacingY={16}
            maxW="5xl"
            mx="auto">
            
            {[
            {
              title: 'Multi-Account GMC Dashboard',
              desc: 'See the health of all your GMC accounts in one place.',
              icon: Layers
            },
            {
              title: 'Product-Level Risk Detection',
              desc: 'Find the exact products putting your account at risk.',
              icon: ShieldAlert
            },
            {
              title: 'Live Product Analysis',
              desc: 'We check your live URLs, not just your feed.',
              icon: Search
            },
            {
              title: 'Smart Alerts',
              desc: 'Get notified when a product becomes risky.',
              icon: Bell
            },
            {
              title: 'Risk Tracking Over Time',
              desc: 'See how changes affect your account safety.',
              icon: TrendingUp
            }].
            map((feature, i) =>
            <MotionBox
              key={i}
              initial={{
                opacity: 0,
                x: i % 2 === 0 ? -20 : 20
              }}
              whileInView={{
                opacity: 1,
                x: 0
              }}
              viewport={{
                once: true
              }}>
              
                <Flex gap={4}>
                  <Flex
                  flexShrink={0}
                  w={12}
                  h={12}
                  borderRadius="xl"
                  bg="rgba(16,185,129,0.1)"
                  border="1px solid"
                  borderColor="rgba(16,185,129,0.2)"
                  align="center"
                  justify="center"
                  mt={1}>
                  
                    <feature.icon color="#10b981" size={24} />
                  </Flex>
                  <Box>
                    <Heading
                    as="h3"
                    fontSize="xl"
                    fontWeight="bold"
                    color="white"
                    mb={2}>
                    
                      {feature.title}
                    </Heading>
                    <Text color="gray.400" fontSize="lg">
                      {feature.desc}
                    </Text>
                  </Box>
                </Flex>
              </MotionBox>
            )}
          </SimpleGrid>
        </Container>
      </Box>

      {/* 6. DIFFERENTIATION */}
      <Box
        as="section"
        py={24}
        bg="brand.card"
        borderY="1px solid"
        borderColor="brand.border">
        
        <Container maxW="container.xl" px={4}>
          <MotionBox
            {...fadeIn}
            maxW="4xl"
            mx="auto"
            textAlign="center"
            mb={16}>
            
            <Heading
              as="h2"
              fontSize={{
                base: '3xl',
                md: '4xl'
              }}
              fontWeight="bold"
              mb={4}>
              
              Most Tools Look at Feeds. <br />
              <Text as="span" color="brand.green">
                We Look at What Google Actually Sees.
              </Text>
            </Heading>
          </MotionBox>

          <SimpleGrid
            columns={{
              base: 1,
              md: 2
            }}
            spacing={8}
            maxW="4xl"
            mx="auto">
            
            <MotionBox
              initial={{
                opacity: 0,
                x: -20
              }}
              whileInView={{
                opacity: 1,
                x: 0
              }}
              viewport={{
                once: true
              }}
              bg="brand.dark"
              border="1px solid"
              borderColor="rgba(239,68,68,0.2)"
              borderRadius="2xl"
              p={8}>
              
              <Heading
                as="h3"
                fontSize="xl"
                fontWeight="bold"
                color="gray.300"
                mb={6}
                borderBottom="1px solid"
                borderColor="brand.border"
                pb={4}>
                
                Most tools:
              </Heading>
              <VStack spacing={4} align="stretch">
                {[
                'Only check your product feed',
                "Miss what's actually on your site",
                'Show issues after disapproval'].
                map((item, i) =>
                <Flex key={i} align="flex-start" gap={3}>
                    <Box flexShrink={0} mt={0.5}>
                      <XCircle color="#f87171" size={20} />
                    </Box>
                    <Text color="gray.400">{item}</Text>
                  </Flex>
                )}
              </VStack>
            </MotionBox>

            <MotionBox
              initial={{
                opacity: 0,
                x: 20
              }}
              whileInView={{
                opacity: 1,
                x: 0
              }}
              viewport={{
                once: true
              }}
              bg="brand.dark"
              border="1px solid"
              borderColor="rgba(16,185,129,0.3)"
              borderRadius="2xl"
              p={8}
              boxShadow="glow">
              
              <Flex
                as="h3"
                fontSize="xl"
                fontWeight="bold"
                color="white"
                mb={6}
                borderBottom="1px solid"
                borderColor="brand.border"
                pb={4}
                align="center"
                gap={2}>
                
                <Radar color="#10b981" size={24} />
                GMCRadar:
              </Flex>
              <VStack spacing={4} align="stretch">
                {[
                'Analyses your live product pages',
                'Flags risky products before enforcement',
                'Works across all your GMC accounts',
                'Shows exactly what needs fixing'].
                map((item, i) =>
                <Flex key={i} align="flex-start" gap={3}>
                    <Box flexShrink={0} mt={0.5}>
                      <CheckCircle2 color="#10b981" size={20} />
                    </Box>
                    <Text color="white" fontWeight="medium">
                      {item}
                    </Text>
                  </Flex>
                )}
              </VStack>
            </MotionBox>
          </SimpleGrid>

          <MotionBox {...fadeIn} textAlign="center" mt={12}>
            <Text
              fontSize={{
                base: 'xl',
                md: '2xl'
              }}
              fontWeight="bold"
              color="gray.300">
              
              This is where most people lose their accounts.{' '}
              <Text as="span" color="brand.green">
                You won't.
              </Text>
            </Text>
          </MotionBox>
        </Container>
      </Box>

      {/* 7. ECOSYSTEM */}
      {/* <Box as="section" py={24} position="relative">
        <Container maxW="container.xl" px={4}>
          <MotionBox {...fadeIn} maxW="5xl" mx="auto">
            <Flex
              direction={{
                base: 'column',
                md: 'row'
              }}
              align="center"
              justify="space-between"
              gap={4}
              position="relative">
              
              <Box
                display={{
                  base: 'none',
                  md: 'block'
                }}
                position="absolute"
                top="50%"
                left={0}
                w="full"
                h="2px"
                bg="brand.border"
                transform="translateY(-50%)"
                zIndex={0} />
              

              {[
              {
                name: 'GMCRadar',
                action: 'detect',
                icon: Search,
                highlight: true
              },
              {
                name: 'Scalelister',
                action: 'fix',
                icon: Wrench,
                highlight: false
              },
              {
                name: 'StealthyGMC',
                action: 'reduce exposure',
                icon: ShieldCheck,
                highlight: false
              },
              {
                name: 'GMCReady',
                action: 'done-for-you',
                icon: Briefcase,
                highlight: false
              }].
              map((item, i) =>
              <Flex
                key={i}
                position="relative"
                zIndex={10}
                bg="brand.dark"
                border="1px solid"
                borderColor={item.highlight ? 'brand.green' : 'brand.border'}
                boxShadow={item.highlight ? 'glow' : 'none'}
                borderRadius="xl"
                p={6}
                textAlign="center"
                w={{
                  base: 'full',
                  md: '48'
                }}
                direction="column"
                align="center">
                
                  <Flex
                  w={12}
                  h={12}
                  borderRadius="full"
                  align="center"
                  justify="center"
                  mb={4}
                  bg={item.highlight ? 'rgba(16,185,129,0.2)' : 'brand.card'}
                  color={item.highlight ? 'brand.green' : 'gray.400'}>
                  
                    <item.icon size={24} />
                  </Flex>
                  <Text
                  fontWeight="bold"
                  mb={1}
                  color={item.highlight ? 'white' : 'gray.300'}>
                  
                    {item.name}
                  </Text>
                  <Text
                  fontSize="xs"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  fontWeight="semibold">
                  
                    → {item.action}
                  </Text>
                </Flex>
              )}
            </Flex>
          </MotionBox>
        </Container>
      </Box> */}

      {/* 8. AUTHORITY / TRUST */}
      <Box
        as="section"
        py={20}
        bg="brand.card"
        borderY="1px solid"
        borderColor="brand.border">
        
        <Container maxW="container.xl" px={4}>
          <MotionBox {...fadeIn} maxW="3xl" mx="auto" textAlign="center">
            <Flex
              flexWrap="wrap"
              justify="center"
              gap={4}
              fontSize="lg"
              fontWeight="medium"
              color="gray.400"
              mb={10}>
              
              <Text as="span">
                Built for stores running aggressive products.
              </Text>
              <Text
                as="span"
                display={{
                  base: 'none',
                  md: 'inline'
                }}>
                
                •
              </Text>
              <Text as="span">Designed from real GMC suspension patterns.</Text>
              <Text
                as="span"
                display={{
                  base: 'none',
                  md: 'inline'
                }}>
                
                •
              </Text>
              <Text as="span">Used by operators managing multiple stores.</Text>
            </Flex>

            <Heading
              as="h2"
              fontSize={{
                base: '3xl',
                md: '4xl'
              }}
              fontWeight="bold">
              
              We don't guess what's risky. <br />
              <Text as="span" color="brand.green">
                We show you.
              </Text>
            </Heading>
          </MotionBox>
        </Container>
      </Box>

      {/* 9. CLOSE */}
      <Box as="section" py={32} position="relative" overflow="hidden">
        <Box position="absolute" inset={0} bg="rgba(16,185,129,0.05)" />
        <Box
          position="absolute"
          bottom={0}
          left="50%"
          transform="translateX(-50%)"
          w="600px"
          h="300px"
          bg="rgba(16,185,129,0.2)"
          filter="blur(100px)"
          borderRadius="full"
          pointerEvents="none" />
        

        <Container maxW="container.xl" px={4} position="relative" zIndex={10}>
          <MotionBox {...fadeIn} maxW="3xl" mx="auto" textAlign="center">
            <Heading
              as="h2"
              fontSize={{
                base: '4xl',
                md: '5xl'
              }}
              fontWeight="bold"
              mb={6}>
              
              It Only Takes One Product To Get Banned
            </Heading>

            <Text fontSize="xl" color="gray.300" mb={12}>
              You don't need to fix everything. <br />
              You just need to fix the right products before Google finds them.
            </Text>

            <Flex
              direction={{
                base: 'column',
                sm: 'row'
              }}
              align="center"
              justify="center"
              gap={4}>
              
              <Button
                as={RouterLink}
                to="/request-access"
                onClick={() => window.scrollTo(0, 0)}
                w={{
                  base: 'full',
                  sm: 'auto'
                }}
                display="inline-flex"
                alignItems="center"
                gap={2}
                px={10}
                py={5}
                h="auto"
                fontSize="xl"
                fontWeight="bold"
                color="brand.dark"
                bg="brand.green"
                borderRadius="xl"
                _hover={{
                  bg: 'brand.greenHover',
                  boxShadow: 'glow-strong',
                  transform: 'scale(1.05)'
                }}
                transition="all 0.2s">
                
                <Text as="span">Request Access</Text>
                <ArrowRight size={24} />
              </Button>
              {/* <Button
                onClick={handleScanNavigate}
                w={{
                  base: 'full',
                  sm: 'auto'
                }}
                display="inline-flex"
                alignItems="center"
                gap={2}
                px={10}
                py={5}
                h="auto"
                fontSize="xl"
                fontWeight="bold"
                color="white"
                bg="transparent"
                border="2px solid"
                borderColor="brand.border"
                borderRadius="xl"
                _hover={{
                  borderColor: 'brand.green',
                  bg: 'rgba(16,185,129,0.05)'
                }}
                transition="all 0.2s">
                
                <Text as="span">Scan My Store Free</Text>
                <Search size={24} />
              </Button> */}
            </Flex>
          </MotionBox>
        </Container>
      </Box>
    </Box>);

}
